import React, { useState, useEffect, useMemo, useRef } from "react";
import { db } from "../../lib/firebase";
import { collection, getDocs, getDoc, doc, updateDoc, query, where } from "firebase/firestore";
import { useNotification } from "@mmfestival/shared-components";
import {
  LuPackage,
  LuSearch,
  LuLoader,
  LuCheck,
  LuBox,
  LuUsers,
  LuActivity,
  LuBarcode,
  LuFilter,
  LuRefreshCw,
} from "react-icons/lu";
import { InputField, Button } from "@mmfestival/shared-components";

// Multi-event racepack desk. The event is chosen from `semuaKegiatan` and the
// registrations live in `pendaftaran<eventId>` (pendaftaranSMM, pendaftaranJMM,
// pendaftaranBMM). For a single-event desk, see /smm/rpc.
const PAID_STATUSES = ["settlement", "capture"];
const PAGE_SIZE = 50;

const normalize = (v) => String(v ?? "").toLowerCase().trim();

// `bibName` and `bibNumber` are two different fields: the participant types
// `bibName` into the "BIB Name" field when registering (MARLON, ZILONG), while
// `bibNumber` is the race number (2044). They are shown separately instead of
// collapsed into one "BIB" field that would mislead the collection desk.
const getBibNumber = (p) => p.bibNumber || "";
const getBibName = (p) => p.bibName || "";

const getCategory = (p) => p.kategori || p.category || "-";
const getName = (p) => p.namaLengkap || p.nama || p.teamName || "-";

// `rpcTimestamp` may be a Firestore Timestamp or a plain date depending on
// which client wrote it, so normalise before display.
const formatTimestamp = (t) => {
  if (!t) return "";
  try {
    if (t.toDate) return t.toDate().toLocaleString("en-US");
    return new Date(t.seconds ? t.seconds * 1000 : t).toLocaleString("en-US");
  } catch {
    return "";
  }
};

const RacePackCollection = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [delegateName, setDelegateName] = useState("");
  const [showDelegateSuggestions, setShowDelegateSuggestions] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { showNotification, showConfirm } = useNotification();
  const searchInputRef = useRef(null);
  const topRef = useRef(null);

  const collectionName = selectedEvent ? `pendaftaran${selectedEvent}` : null;

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    setSelectedParticipant(null);
    setListSearch("");
    setSearchQuery("");
    setVisibleCount(PAGE_SIZE);
    fetchParticipants();
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [selectedEvent]);

  useEffect(() => {
    setDelegateName("");
    setShowDelegateSuggestions(false);
  }, [selectedParticipant]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [listSearch, statusFilter]);

  const fetchEvents = async () => {
    try {
      const snapshot = await getDocs(collection(db, "semuaKegiatan"));
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEvents(list);
      // Default to the first event so the page is never stuck on an id that
      // has no matching pendaftaran collection.
      if (list.length > 0) setSelectedEvent((prev) => prev || list[0].id);
      else setLoading(false);
    } catch (error) {
      console.error("Error fetching events:", error);
      showNotification("error", "Failed to load events");
      setLoading(false);
    }
  };

  const enrichWithAvatars = async (list) => {
    const uids = [...new Set(list.map((p) => p.uid).filter(Boolean))];
    if (!uids.length) return list;
    const photoMap = {};
    await Promise.all(
      uids.map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          if (snap.exists()) photoMap[uid] = snap.data().photoURL || snap.data().photoUrl || null;
        } catch {}
      })
    );
    return list.map((p) => ({ ...p, photoURL: photoMap[p.uid] || p.photoURL || p.photoUrl || null }));
  };

  const fetchParticipants = async () => {
    if (!collectionName) return;
    setLoading(true);
    try {
      const snapshots = await Promise.all(
        PAID_STATUSES.map((status) =>
          getDocs(query(collection(db, collectionName), where("transaction_status", "==", status)))
        )
      );

      const seen = new Set();
      let list = [];
      snapshots.forEach((snapshot) => {
        snapshot.docs.forEach((d) => {
          if (seen.has(d.id)) return;
          seen.add(d.id);
          list.push({ id: d.id, ...d.data() });
        });
      });

      list = await enrichWithAvatars(list);
      list.sort((a, b) => getName(a).localeCompare(getName(b)));
      setParticipants(list);
    } catch (error) {
      console.error("Error fetching participants:", error);
      showNotification("error", "Failed to load participants");
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = participants.length;
    const collected = participants.filter((p) => p.racePackCollected).length;
    return { total, collected, pending: total - collected };
  }, [participants]);

  const eventOptions = useMemo(
    () => events.map((e) => ({ value: e.id, label: e.title || e.id })),
    [events]
  );

  // Ranked, not just filtered: a short BIB like "103" also appears inside dozens
  // of identity numbers, and the desk searching for that BIB would otherwise
  // find it buried far below the 25-row cut-off. Exact BIB wins, then BIB/name
  // prefixes, and identity-number hits sink to the bottom.
  const suggestions = useMemo(() => {
    const q = normalize(searchQuery);
    if (q.length < 2) return [];
    const score = (p) => {
      const bibNo = normalize(getBibNumber(p));
      const name = normalize(getName(p));
      const bibName = normalize(getBibName(p));
      if (bibNo === q) return 0;
      if (normalize(p.orderId) === q) return 1;
      if (bibName === q || name === q) return 2;
      if (bibNo.startsWith(q)) return 3;
      if (name.startsWith(q) || bibName.startsWith(q)) return 4;
      if (name.includes(q) || bibName.includes(q)) return 5;
      if (normalize(p.orderId).includes(q)) return 6;
      if (normalize(p.email).includes(q)) return 7;
      return 8; // identity-number-only match
    };
    return participants
      .filter((p) =>
        [getName(p), p.orderId, getBibNumber(p), getBibName(p), p.email, p.nomorIdentitas].some(
          (f) => normalize(f).includes(q)
        )
      )
      .map((p) => ({ p, s: score(p) }))
      .sort((a, b) => a.s - b.s)
      .slice(0, 25)
      .map((x) => x.p);
  }, [participants, searchQuery]);

  const handleScan = () => {
    const q = normalize(searchQuery);
    if (!q) return;

    // Order ID is unique per registration, so it is the only identifier safe to
    // auto-select on. Jersey names and participant names collide in the real
    // data, so those must be disambiguated by the operator.
    const byOrderId = participants.filter((p) => normalize(p.orderId) === q);
    if (byOrderId.length === 1) {
      setSelectedParticipant(byOrderId[0]);
      setSearchQuery("");
      if (searchInputRef.current) searchInputRef.current.focus();
      return;
    }

    const exact = participants.filter((p) =>
      [getBibNumber(p), getBibName(p), p.nomorIdentitas, getName(p)].some(
        (f) => f && normalize(f) === q
      )
    );
    const candidates = exact.length > 0 ? exact : suggestions;

    if (candidates.length === 1) {
      setSelectedParticipant(candidates[0]);
      setSearchQuery("");
      if (searchInputRef.current) searchInputRef.current.focus();
    } else if (candidates.length > 1) {
      showNotification(
        "info",
        `${candidates.length} participants match "${searchQuery.trim()}" - pick the right one from the list.`
      );
    } else {
      showNotification("info", "No paid participant found matching that ID");
    }
  };

  const applyLocalUpdate = (participant, patch) => {
    setParticipants((prev) => prev.map((p) => (p.id === participant.id ? { ...p, ...patch } : p)));
    setSelectedParticipant((prev) =>
      prev && prev.id === participant.id ? { ...prev, ...patch } : prev
    );
  };

  const handleMarkCollected = async (participant) => {
    setProcessing(true);
    try {
      const patch = {
        racePackCollected: true,
        rpcTimestamp: new Date(),
        rpcDelegateName: delegateName.trim() || null,
      };
      await updateDoc(doc(db, collectionName, participant.id), patch);
      applyLocalUpdate(participant, patch);
    } catch (error) {
      console.error("Error marking collected:", error);
      showNotification("error", "Failed to update record");
    } finally {
      setProcessing(false);
    }
  };

  const handleUndoCollection = (participant) => {
    showConfirm(
      "Undo this collection? Data will be reset.",
      () => _doUndoCollection(participant),
      "Undo",
      "Cancel"
    );
  };

  const _doUndoCollection = async (participant) => {
    setProcessing(true);
    try {
      const patch = { racePackCollected: false, rpcTimestamp: null, rpcDelegateName: null };
      await updateDoc(doc(db, collectionName, participant.id), patch);
      applyLocalUpdate(participant, patch);
    } catch (error) {
      console.error("Error undoing:", error);
      showNotification("error", "Failed to undo record");
    } finally {
      setProcessing(false);
    }
  };

  const filteredList = useMemo(() => {
    let list = participants;
    if (statusFilter === "collected") list = list.filter((p) => p.racePackCollected);
    if (statusFilter === "awaiting") list = list.filter((p) => !p.racePackCollected);
    const q = normalize(listSearch);
    if (q) {
      list = list.filter((p) =>
        [getName(p), p.email, getBibNumber(p), getBibName(p), p.orderId].some((f) =>
          normalize(f).includes(q)
        )
      );
    }
    return list;
  }, [participants, statusFilter, listSearch]);

  const visibleList = filteredList.slice(0, visibleCount);
  const isLastPage = visibleCount >= filteredList.length;
  const currentEventLabel =
    events.find((e) => e.id === selectedEvent)?.title || selectedEvent || "—";

  return (
    <>
      <div ref={topRef} />
      <div className="space-y-4 pb-20">
        {/* Header */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuPackage size={120} />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start gap-4">
            <div>
              <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                <LuActivity size={14} /> RPC Management — {currentEventLabel}
              </p>
              <h1 className="text-2xl md:text-3xl font-bold mb-1">Racepack Collection</h1>
              <p className="text-slate-500 text-sm max-w-xl">
                Search participants and mark their racepack as collected.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchParticipants}
                disabled={loading || !selectedEvent}
                className="flex items-center gap-2 bg-white text-slate-600 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                <LuRefreshCw size={13} className={loading ? "animate-spin" : ""} />
                {loading ? "Refreshing" : "Refresh"}
              </button>
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold border border-green-100">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                USB SCANNER READY
              </div>
            </div>
          </div>
        </div>

        {/* Split Pane: Scan and Details */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-col-reverse">
          {/* Left Card: Scanner */}
          <div className="lg:col-span-5 bg-white rounded-xl border border-slate-200 p-6 flex flex-col items-center justify-center min-h-[250px]">
            <div className="mb-6">
              <LuBarcode size={60} className="text-slate-300" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2 text-center">Scan E-Ticket</h2>
            <p className="text-slate-400 text-center text-sm mb-6">
              Focus input and scan QR Code or type ID
            </p>

            <div className="w-full max-w-sm relative">
              <InputField
                ref={searchInputRef}
                name="scan"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleScan()}
                placeholder="Scan QR or type name/ID..."
                startIcon={<LuSearch className="text-slate-400" />}
                labelClassName="sr-only"
                autoComplete="off"
                disabled={!selectedEvent}
              />

              {searchQuery.length >= 2 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden max-h-[300px] overflow-y-auto custom-scrollbar">
                  {suggestions.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => {
                        setSelectedParticipant(p);
                        setSearchQuery("");
                      }}
                      className="p-3 border-b border-slate-50 last:border-none hover:bg-slate-50 cursor-pointer flex justify-between items-center transition-colors"
                    >
                      <div>
                        <p className="font-bold text-slate-800 text-sm leading-tight">{getName(p)}</p>
                        <p className="text-[10px] text-slate-400 font-medium uppercase mt-0.5">
                          {getCategory(p)}
                        </p>
                        {p.orderId && (
                          <p className="text-[10px] text-slate-400 font-mono mt-0.5">{p.orderId}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-800 text-sm tracking-tight">
                          {getBibNumber(p) || getBibName(p) || "-"}
                        </p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">
                          {p.sizeJersey || "-"}
                        </p>
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="p-4 flex items-center justify-center gap-2 text-xs text-slate-400 italic">
                      <LuLoader className="animate-spin" /> Loading…
                    </div>
                  )}
                  {!loading && suggestions.length === 0 && (
                    <div className="p-4 text-center text-xs text-slate-400 italic">
                      No matching participants
                    </div>
                  )}
                </div>
              )}

              <p className="text-[10px] text-slate-300 mt-2 text-center uppercase tracking-widest font-medium">
                Auto-detects REG-ID, URL, or Name
              </p>
            </div>
          </div>

          {/* Right Card: Details */}
          <div className="lg:col-span-7 bg-white rounded-xl border border-slate-200 min-h-[250px] relative">
            {!selectedParticipant ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center opacity-40">
                <LuBox size={60} className="text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-500">No Participant Selected</h3>
                <p className="text-slate-400 text-sm">Scan or search to view details</p>
              </div>
            ) : (
              <div className="h-full flex flex-col">
                {selectedParticipant.racePackCollected ? (
                  <div className="bg-green-50 border-b border-green-100 p-3 flex flex-col gap-2 text-green-700 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <LuPackage className="text-green-500" />
                        <p className="font-bold text-sm uppercase">Already Collected</p>
                      </div>
                      <p className="text-[10px] opacity-70 font-medium">
                        {formatTimestamp(selectedParticipant.rpcTimestamp)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 border-b border-slate-100 p-3 flex items-center gap-3 text-slate-500 rounded-t-xl">
                    <LuPackage />
                    <p className="font-bold text-sm uppercase tracking-wide">Ready for Pickup</p>
                  </div>
                )}

                <div className="p-4 flex-1">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                    <div className="col-span-2 md:col-span-1 border-r border-slate-100 pr-4">
                      <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">
                        Participant
                      </p>
                      <h3 className="text-lg font-bold text-slate-800 leading-tight mb-0.5">
                        {getName(selectedParticipant)}
                      </h3>
                      <p className="text-slate-500 text-xs">
                        {selectedParticipant.email || selectedParticipant.nomorIdentitas || "-"}
                      </p>

                      <div className="mt-4 space-y-3">
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">
                            Category
                          </p>
                          <p className="text-sm font-medium text-slate-700">
                            {getCategory(selectedParticipant)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">
                            Gender
                          </p>
                          <p className="text-sm font-medium text-slate-700 uppercase">
                            {selectedParticipant.gender || "-"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-2 md:col-span-1 flex flex-col justify-between space-y-4">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">
                          Jersey Size
                        </p>
                        <div className="text-3xl font-bold text-slate-800">
                          {selectedParticipant.sizeJersey || "?"}
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-0.5">
                          BIB Number
                        </p>
                        <p className="text-3xl font-bold text-slate-800">
                          {getBibNumber(selectedParticipant) || "—"}
                        </p>
                        {getBibName(selectedParticipant) && (
                          <>
                            <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-0.5 mt-3">
                              BIB Name
                            </p>
                            <p className="text-lg font-semibold text-slate-700">
                              {getBibName(selectedParticipant)}
                            </p>
                          </>
                        )}
                      </div>

                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">
                          Order ID
                        </p>
                        <p className="text-sm font-medium text-slate-700 font-mono">
                          {selectedParticipant.orderId || "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2">
                    {selectedParticipant.racePackCollected &&
                    selectedParticipant.rpcDelegateName ? (
                      <div className="mb-4">
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">
                          Collected By
                        </p>
                        <p className="text-sm font-medium text-slate-700 uppercase">
                          {selectedParticipant.rpcDelegateName}
                        </p>
                      </div>
                    ) : !selectedParticipant.racePackCollected ? (
                      <div className="mb-4 relative">
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-2">
                          Collected By (Optional)
                        </p>
                        <InputField
                          name="delegateName"
                          value={delegateName}
                          onChange={(e) => {
                            setDelegateName(e.target.value);
                            setShowDelegateSuggestions(true);
                          }}
                          placeholder="Name of delegate (optional)..."
                          labelClassName="sr-only"
                          autoComplete="off"
                        />
                        {showDelegateSuggestions &&
                          delegateName.length >= 2 &&
                          participants.filter(
                            (p) =>
                              normalize(getName(p)).includes(normalize(delegateName)) &&
                              p.id !== selectedParticipant.id
                          ).length > 0 && (
                            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden max-h-[150px] overflow-y-auto custom-scrollbar">
                              {participants
                                .filter(
                                  (p) =>
                                    normalize(getName(p)).includes(normalize(delegateName)) &&
                                    p.id !== selectedParticipant.id
                                )
                                .slice(0, 3)
                                .map((p) => (
                                  <div
                                    key={p.id}
                                    onClick={() => {
                                      setDelegateName(getName(p));
                                      setShowDelegateSuggestions(false);
                                    }}
                                    className="p-3 border-b border-slate-50 last:border-none hover:bg-slate-50 cursor-pointer transition-colors"
                                  >
                                    <p className="font-bold text-slate-800 text-sm leading-tight">
                                      {getName(p)}
                                    </p>
                                    <p className="text-[10px] text-slate-400 font-medium uppercase mt-0.5">
                                      {getCategory(p)}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          )}
                      </div>
                    ) : null}

                    <div className="flex gap-4">
                      <Button
                        onClick={() => setSelectedParticipant(null)}
                        className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700"
                      >
                        Cancel
                      </Button>

                      {selectedParticipant.racePackCollected ? (
                        <Button
                          onClick={() => handleUndoCollection(selectedParticipant)}
                          disabled={processing}
                          // `!` is required here: Button ships base bg/text
                          // classes that sit later in the stylesheet and would
                          // otherwise win over these.
                          className="flex-1 !bg-red-50 hover:!bg-red-100 !text-red-600 uppercase tracking-widest"
                        >
                          Undo
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleMarkCollected(selectedParticipant)}
                          disabled={processing}
                          className="flex-1 bg-slate-800 hover:bg-slate-900 text-white uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {processing ? (
                            <>
                              <LuLoader className="animate-spin text-xs" /> Processing
                            </>
                          ) : (
                            "Confirm"
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
          <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
              <LuUsers size={20} />
            </div>
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Total Paid</p>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.total}</h3>
              <p className="text-sm text-slate-400 mt-1">Paid participants</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
              <LuCheck size={20} />
            </div>
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Collected</p>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">
                {stats.collected}
              </h3>
              <p className="text-sm text-slate-400 mt-1">Packets picked up</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
              <LuBox size={20} />
            </div>
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Pending</p>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.pending}</h3>
              <p className="text-sm text-slate-400 mt-1">Awaiting collection</p>
            </div>
          </div>
        </div>

        {/* Filters Area */}
        <div className="bg-white rounded-xl p-4 border">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <InputField
                name="listSearch"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Search Name, Email, Order ID, or Jersey Name..."
                startIcon={<LuSearch className="text-slate-400" />}
                labelClassName="sr-only"
              />
            </div>

            {/* Event lives with the other filters so the three controls share one
                balanced row instead of a near-empty card of its own. */}
            <div className="min-w-[200px]">
              <InputField
                type="select"
                name="selectedEvent"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                options={eventOptions}
                defaultOptionLabel="Select Event"
                labelClassName="sr-only"
              />
            </div>

            <div className="min-w-[200px]">
              <InputField
                type="select"
                name="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "collected", label: "Collected" },
                  { value: "awaiting", label: "Awaiting" },
                ]}
                startIcon={<LuFilter className="text-slate-400" />}
                defaultOptionLabel="Filter Status"
                labelClassName="sr-only"
              />
            </div>
          </div>
        </div>

        {/* Participant Card List */}
        <div className="flex flex-col gap-2">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <LuLoader className="animate-spin text-slate-400" size={32} />
            </div>
          ) : visibleList.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 px-4 py-12 text-center text-slate-400 italic text-sm">
              No paid participants found.
            </div>
          ) : (
            visibleList.map((p) => (
              <div
                key={p.id}
                className="bg-white rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => {
                  setSelectedParticipant(p);
                  topRef.current?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-lime-100 flex items-center justify-center text-slate-700 font-bold flex-shrink-0 overflow-hidden">
                      {p.photoURL ? (
                        <img
                          src={p.photoURL}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <span
                        style={{ display: p.photoURL ? "none" : "flex" }}
                        className="w-full h-full items-center justify-center"
                      >
                        {getName(p).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{getName(p)}</p>
                      <p className="text-[10px] text-slate-400 font-mono truncate">{p.orderId}</p>
                    </div>
                  </div>

                  <div className="col-span-3">
                    <p className="text-sm font-medium text-slate-700 truncate">{getCategory(p)}</p>
                    {p.racePackCollected && p.rpcDelegateName && (
                      <p className="text-[10px] text-slate-500 font-bold uppercase truncate mt-0.5">
                        {p.rpcDelegateName}
                      </p>
                    )}
                  </div>

                  <div className="col-span-2 text-center">
                    <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">Jersey</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {p.sizeJersey || "-"}
                      {getBibNumber(p) ? ` · ${getBibNumber(p)}` : ""}
                    </p>
                    {getBibName(p) && (
                      <p className="text-[10px] text-slate-400 truncate">{getBibName(p)}</p>
                    )}
                  </div>

                  <div className="col-span-2 flex justify-center">
                    {p.racePackCollected ? (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                        <LuCheck size={10} /> Collected
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                        Awaiting
                      </span>
                    )}
                  </div>

                  <div className="col-span-1 flex justify-end">
                    <button className="text-[10px] font-bold uppercase text-slate-400 hover:text-slate-800 transition-colors">
                      Select
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {!loading && !isLastPage && (
          <div className="flex justify-end mt-2">
            <Button
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700 px-6 py-2 h-auto text-sm font-medium flex items-center gap-2 transition-all"
            >
              Load More
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default RacePackCollection;
